import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const buildLoginRedirectUrl = (redirectPath: string): string =>
  `/login?redirect=${encodeURIComponent(redirectPath)}`;

const PLAIN_LOGIN_REDIRECT_KEY = "plainLoginRedirect";

const isForcedLogout = (): boolean =>
  sessionStorage.getItem("logoutInProgress") === "1";

const shouldUsePlainLoginRedirect = (message: unknown): boolean => {
  const normalizedMessage = String(message || "").toLowerCase();
  return (
    isForcedLogout() ||
    normalizedMessage.includes("another device") ||
    normalizedMessage.includes("single device limit") ||
    normalizedMessage.includes("device limit")
  );
};

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // increased for video streams
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // send cookies if any
});

// Request interceptor: attach token if available
apiClient.interceptors.request.use(
  (config) => {
    const token =
      sessionStorage.getItem("token") || localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: handle 401 globally
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const responseMessage = error.response?.data?.message;
      // Clear all session data
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("refreshToken");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("redirectAfterLogin");
      sessionStorage.removeItem("loginRedirectTarget");
      sessionStorage.removeItem("redirectAfterSubscription");
      sessionStorage.removeItem("authRedirecting");
      localStorage.removeItem("token");
      localStorage.removeItem("authToken");

      if (shouldUsePlainLoginRedirect(responseMessage)) {
        sessionStorage.setItem(PLAIN_LOGIN_REDIRECT_KEY, "1");
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }

      sessionStorage.removeItem(PLAIN_LOGIN_REDIRECT_KEY);
      const redirectAfterLogin = window.location.pathname.startsWith(
        "/home/courses",
      )
        ? "/subscription"
        : window.location.pathname;
      sessionStorage.setItem("redirectAfterLogin", redirectAfterLogin);

      // Redirect to login if not already there
      if (window.location.pathname !== "/login") {
        window.location.href = buildLoginRedirectUrl(redirectAfterLogin);
      }
    }
    return Promise.reject(error);
  },
);

// Helper to update base URL if needed
export const setApiBaseUrl = (url: string) => {
  apiClient.defaults.baseURL = url;
};

export default apiClient;
